import {
  AppstoreOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  PlusSquareOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import PropTypes from 'prop-types';
import {useMemo, useRef, useState} from 'react';
import {FaCheck} from 'react-icons/fa';
import {FaTruckFast} from 'react-icons/fa6';
import {MdOutlineInventory} from 'react-icons/md';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../CommonComponents/Cards';
import {TableData} from '../../../CommonComponents/TableData';
import {
  RqDeleteInventory,
  RqDeleteInventoryLot,
  RqGetAllInventory,
  RqRegisterInventory,
  RqUpdateInventoryLot,
} from '../../../reactQuery/apis/rqInventoryAPI';
import {selectAuth} from '../../../redux/selectors';
import {formatInventoryNumber} from '../../../utils/inventoryUtils';

import {AddLotView} from './AddLotScreen';

dayjs.extend(isBetween);

const {Title, Text} = Typography;
const {RangePicker} = DatePicker;

const InventoryListView = ({onAddLot}) => {
  const token = useSelector(selectAuth)?.tokenData;
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);

  const [selectedLot, setSelectedLot] = useState(null);
  const [isLotModalOpen, setIsLotModalOpen] = useState(false);

  // State for Edit Lot Modal
  const [editingLot, setEditingLot] = useState(null);
  const [editingInventory, setEditingInventory] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const printRef = useRef(null);

  const {data: rawInventory = [], isLoading} = RqGetAllInventory(token);

  const inventoryList = useMemo(() => {
    const sorted = [...rawInventory].sort(
      (a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0),
    );
    return sorted.map((item, index, arr) => {
      const count = item?.inventoryCount ?? (arr.length - index);
      const inventoryNumber = item?.inventoryNumber || formatInventoryNumber(item, count);
      return {
        ...item,
        inventoryCount: count,
        inventoryNumber,
      };
    });
  }, [rawInventory]);

  const [startDate, endDate] = dateRange;

  const filtered = inventoryList.filter(p => {
    const matchSearch =
      !searchText ||
      p.productName?.toLowerCase().includes(searchText.toLowerCase()) ||
      p.category?.toLowerCase().includes(searchText.toLowerCase()) ||
      p.barcode?.toLowerCase().includes(searchText.toLowerCase());

    const matchDate =
      !startDate ||
      !endDate ||
      dayjs(p.createdOn).isBetween(startDate, endDate, 'day', '[]');

    return matchSearch && matchDate;
  });

  const handleClearFilters = () => {
    setSearchText('');
    setDateRange([null, null]);
  };

  const deleteLotMutation = RqDeleteInventoryLot();
  const deleteInventoryMutation = RqDeleteInventory();
  const updateLotMutation = RqUpdateInventoryLot();

  const handleDeleteLot = lot => {
    deleteLotMutation.mutate(
      {lotId: lot.lotId, token},
      {
        onSuccess: () => {
          message.success('Lot deleted successfully');
        },
        onError: error => {
          message.error(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to delete lot',
          );
        },
      },
    );
  };

  const deleteInventory = inventory => {
    deleteInventoryMutation.mutate(
      {id: inventory.id, token},
      {
        onSuccess: () => {
          message.success('Inventory deleted successfully');
        },
        onError: error => {
          message.error(
            error?.response?.data?.message ||
              error?.message ||
              'Failed to delete inventory',
          );
        },
      },
    );
  };

  const handleViewLot = lot => {
    setSelectedLot(lot);
    setIsLotModalOpen(true);
  };

  const handleCloseLotModal = () => {
    setIsLotModalOpen(false);
    setSelectedLot(null);
  };

  // Handlers for Edit Lot
  const handleEditLot = (lot, inventory) => {
    setEditingLot(lot);
    setEditingInventory(inventory);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingLot(null);
    setEditingInventory(null);
  };

  const getLotColumns = parentInventory => [
    {
      title: 'Lot #',
      dataIndex: 'lotNumber',
      key: 'lotNumber',
      width: 130,
      render: v => <p className={'text-sm'}>{v || '—'}</p>,
    },
    {title: 'Supplier', dataIndex: 'supplierName', key: 'supplier', width: 150},
    {title: 'Warehouse', dataIndex: 'warehouse', key: 'warehouse', width: 130},
    {
      title: 'Products',
      key: 'products',
      width: 80,
      align: 'center',
      render: (_, row) => (
        <Badge count={row.products?.length || 0} showZero color={'#185FA5'} />
      ),
    },
    {
      title: 'Grand total',
      key: 'grandTotal',
      width: 120,
      align: 'right',
      render: (_, row) =>
        row.bill?.grandTotal != null ? (
          <Text strong>₹{row.bill.grandTotal.toFixed(2)}</Text>
        ) : (
          '—'
        ),
    },
    {
      title: 'Invoice #',
      dataIndex: ['bill', 'invoiceNumber'],
      key: 'invoice',
      width: 140,
      render: v => <Text className={'text-sm text-gray-400'}>{v || '—'}</Text>,
    },
    {
      title: 'Action',
      key: 'lotActions',
      width: 120,
      align: 'center',
      render: (_, row) => (
        <div className={'flex items-center justify-center gap-2'}>
          <Tooltip title={'View lot'}>
            <Button
              size={'small'}
              icon={<EyeOutlined className={'text-slate-700'} />}
              onClick={() => handleViewLot(row)}
              className={'flex items-center justify-center border-gray-300 bg-white text-slate-700 hover:!text-slate-900 hover:!border-gray-400'}
            />
          </Tooltip>

          {/* Edit Lot Button */}
          <Tooltip title={'Edit lot'}>
            <Button
              size={'small'}
              icon={<EditOutlined className={'text-blue-600'} />}
              onClick={() => handleEditLot(row, parentInventory)}
              className={'flex items-center justify-center border-gray-300 bg-white hover:!border-gray-400'}
            />
          </Tooltip>

          {/* Delete Lot Button */}
          <Popconfirm
            title={'Delete this lot?'}
            description={'This action cannot be undone.'}
            okText={'Delete'}
            cancelText={'Cancel'}
            okButtonProps={{danger: true}}
            onConfirm={() => handleDeleteLot(row)}
          >
            <Tooltip title={'Delete lot'}>
              <Button
                size={'small'}
                danger
                icon={<DeleteOutlined />}
                loading={
                  deleteLotMutation.isPending &&
                  deleteLotMutation.variables?.lotId === row.lotId
                }
              />
            </Tooltip>
          </Popconfirm>
        </div>
      ),
    },
  ];

  const lotProductColumns = [
    {
      title: 'Product name',
      key: 'pname',
      render: (_, record) => {
        const pName =
          record?.productName ||
          record?.name ||
          record?.product_name ||
          record?.title ||
          record?.product?.name;
        return <Text strong>{pName || '—'}</Text>;
      },
    },
    {
      title: 'Product ID',
      dataIndex: 'productId',
      key: 'pid',
      render: v => (
        <Text code className={'text-xs'}>
          {v || '—'}
        </Text>
      ),
    },
    {title: 'Qty', dataIndex: 'quantity', key: 'qty', align: 'right'},
    {title: 'Unit', dataIndex: 'unit', key: 'unit'},
    {
      title: 'Purchase ₹',
      dataIndex: 'purchasePrice',
      key: 'pp',
      align: 'right',
      render: v => (v != null ? `₹${v}` : '—'),
    },
    {
      title: 'Selling ₹',
      dataIndex: 'sellingPrice',
      key: 'sp',
      align: 'right',
      render: v => (v != null ? `₹${v}` : '—'),
    },
    {title: 'HSN', dataIndex: 'hsnCode', key: 'hsn'},
    {
      title: 'Barcode',
      dataIndex: 'barcode',
      key: 'barcode',
      render: v => (
        <Text code className={'text-xs'}>
          {v || '—'}
        </Text>
      ),
    },
  ];

  const columns = [
    {
      title: 'Sr No.',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Inventory',
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      render: (name, row) => {
        const invNum = row?.inventoryNumber || formatInventoryNumber(row);
        return (
          <div>
            <Text strong>{name || 'Inventory'}</Text>
            <div className={'text-gray-400 mt-0.5'}>{invNum}</div>
          </div>
        );
      },
    },
    {
      title: 'Lots',
      key: 'lots',
      width: 70,
      align: 'center',
      render: (_, row) => (
        <Badge count={row?.lots?.length || 0} showZero color={'#185FA5'} />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: status => (
        <Tag color={status === 'ACTIVE' ? 'green' : 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 120,
      render: v =>
        v
          ? new Date(v).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })
          : '—',
    },
    {
      title: 'Action',
      key: 'actions',
      width: 110,
      render: (_, row) => (
        <space className={'flex gap-2'}>
          <Tooltip title={'Add lot to this inventory'}>
            <Button
              size={'small'}
              type={'primary'}
              icon={<PlusSquareOutlined />}
              onClick={() => onAddLot(row)}
              style={{background: '#3a5f98'}}
            >
              Add Lot
            </Button>
          </Tooltip>
          <Popconfirm
            title={'Delete this Inventory?'}
            description={'This action cannot be undone.'}
            okText={'Delete'}
            cancelText={'Cancel'}
            okButtonProps={{danger: true}}
            onConfirm={() => deleteInventory(row)}
          >
            <Tooltip title={'Delete Inventory'}>
              <Button
                size={'small'}
                danger
                icon={<DeleteOutlined />}
                loading={
                  deleteInventoryMutation.isPending &&
                  deleteInventoryMutation.variables?.inventoryId === row.id
                }
              />
            </Tooltip>
          </Popconfirm>
        </space>
      ),
    },
  ];

  const hasActiveFilters = searchText || (startDate && endDate);

  return (
    <div>
      <div className={'flex gap-3 mb-4 flex-wrap items-center'}>
        <Input
          prefix={<SearchOutlined className={'text-primary-400'} />}
          placeholder={'Search by name, category, barcode...'}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
          style={{maxWidth: 280}}
        />

        <RangePicker
          value={[
            startDate ? dayjs(startDate) : null,
            endDate ? dayjs(endDate) : null,
          ]}
          onChange={dates =>
            setDateRange(dates ? [dates[0], dates[1]] : [null, null])
          }
          format={'DD MMM YYYY'}
          allowClear
          placeholder={['From date', 'To date']}
          style={{width: 260}}
        />

        {hasActiveFilters && (
          <Button
            onClick={handleClearFilters}
            size={'small'}
            className={'text-gray-500'}
          >
            Clear filters
          </Button>
        )}

        <Text type={'secondary'} className={'text-xs ml-auto'}>
          Showing <b>{filtered.length}</b> to <b>{inventoryList.length}</b>
        </Text>
      </div>

      <TableData
        dataSource={filtered}
        columns={columns}
        rowKey={'id'}
        loading={isLoading}
        size={'middle'}
        expandable={{
          expandIconColumnIndex: 6,
          expandedRowRender: record =>
            record?.lots?.length ? (
              <div className={'py-2 bg-primary-50'}>
                <TableData
                  dataSource={record.lots}
                  columns={getLotColumns(record)}
                  pagination={false}
                  rowKey={'lotId'}
                  className={'rounded'}
                  expandable={{
                    expandIconColumnIndex: 7,
                    expandIcon: ({expanded, onExpand, record: lotRecord}) =>
                      lotRecord?.products?.length ? (
                        <Tooltip
                          title={expanded ? 'Collapse products' : 'Expand products'}
                        >
                          <Button
                            size={'small'}
                            icon={(
                              <EyeOutlined
                                className={
                                  expanded ? 'text-blue-600' : 'text-slate-700'
                                }
                              />
                            )}
                            onClick={e => onExpand(lotRecord, e)}
                            className={'flex items-center justify-center border-gray-300 bg-white hover:!border-gray-400'}
                          />
                        </Tooltip>
                      ) : null,
                    expandedRowRender: lot =>
                      lot?.products?.length ? (
                        <div className={'py-2'}>
                          <p className={'text-xs font-semibold text-gray-500 uppercase mb-2'}>
                            Products in lot
                          </p>
                          <TableData
                            dataSource={lot.products}
                            columns={lotProductColumns}
                            rowKey={(r, i) => r?.productId || i}
                            className={'rounded mt-5'}
                          />
                        </div>
                      ) : (
                        <div className={'pl-6 py-2 text-sm text-gray-400'}>
                          No products in this lot.
                        </div>
                      ),
                    rowExpandable: lot => !!lot?.products?.length,
                  }}
                />
              </div>
            ) : (
              <div className={'pl-6 py-3 text-sm text-gray-400'}>
                No lots added yet.
              </div>
            ),
          rowExpandable: () => true,
        }}
        scroll={{x: 1100}}
      />

      {/* Lot Details Modal */}
      <Modal
        title={'Lot Details'}
        open={isLotModalOpen}
        onCancel={handleCloseLotModal}
        footer={[
          <Button key={'close'} onClick={handleCloseLotModal}>
            Close
          </Button>,
        ]}
        width={900}
      >
        <div ref={printRef} className={'space-y-5 invoice-print-area p-2'}>
          {selectedLot && (
            <>
              <div>
                <div className={'grid grid-cols-2 gap-4 border border-dashed rounded-xl p-4 w-full'}>
                  <div>
                    <Text type={'secondary'}>Lot Number</Text>
                    <div className={'mt-1'}>
                      <Text code strong>
                        {selectedLot?.lotNumber || '—'}
                      </Text>
                    </div>
                  </div>
                  <div>
                    <Text type={'secondary'}>Supplier</Text>
                    <div className={'mt-1'}>
                      <Text>{selectedLot?.supplierName || '—'}</Text>
                    </div>
                  </div>
                  <div>
                    <Text type={'secondary'}>Warehouse</Text>
                    <div className={'mt-1'}>
                      <Text>{selectedLot?.warehouse || '—'}</Text>
                    </div>
                  </div>
                  <div>
                    <Text type={'secondary'}>Invoice Number</Text>
                    <div className={'mt-1'}>
                      <Text code>{selectedLot?.bill?.invoiceNumber || '—'}</Text>
                    </div>
                  </div>
                  <div>
                    <Text type={'secondary'}>Grand Total</Text>
                    <div className={'mt-1'}>
                      <Text strong>
                        {selectedLot?.bill?.grandTotal != null
                          ? `₹${selectedLot.bill.grandTotal.toFixed(2)}`
                          : '—'}
                      </Text>
                    </div>
                  </div>
                  <div>
                    <Text type={'secondary'}>Number of Products</Text>
                    <div className={'mt-1'}>
                      <Badge
                        count={selectedLot?.products?.length || 0}
                        showZero
                        color={'#185FA5'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Title level={5} className={'!mb-3'}>
                  Products in Lot
                </Title>
                {selectedLot?.products?.length ? (
                  <TableData
                    dataSource={selectedLot.products}
                    columns={lotProductColumns}
                    rowKey={(r, i) => r?.productId || i}
                    pagination={false}
                    className={'rounded'}
                  />
                ) : (
                  <div className={'py-6 text-center text-gray-400'}>
                    No products in this lot.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Edit Lot Modal */}
      <Modal
        title={'Edit Lot'}
        open={isEditModalOpen}
        onCancel={handleCloseEditModal}
        footer={null}
        width={1000}
        destroyOnHidden
      >
        {editingLot && (
          <AddLotView
            key={editingLot.lotId}
            inventoryItem={editingInventory || {}}
            initialData={editingLot}
            isEditMode
            onBack={handleCloseEditModal}
            onSuccess={values => {
              updateLotMutation.mutate(
                {
                  lotId: editingLot.lotId,
                  payload: values,
                  token,
                },
                {
                  onSuccess: () => {
                    handleCloseEditModal();
                    message.success('Lot updated successfully');
                  },
                  onError: error => {
                    message.error(error?.response?.data?.message || error?.message || 'Failed to update lot');
                  },
                },
              );
            }}
          />
        )}
      </Modal>
    </div>
  );
};

InventoryListView.propTypes = {
  onAddLot: PropTypes.func,
};

const InventoryScreen = () => {
  const token = useSelector(selectAuth)?.tokenData;
  const [view, setView] = useState('list');
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null);

  const {data: rawInventory = [], refetch} = RqGetAllInventory(token);

  const inventoryList = useMemo(() => {
    const sorted = [...rawInventory].sort(
      (a, b) => new Date(b.createdOn || 0) - new Date(a.createdOn || 0),
    );
    return sorted.map((item, index, arr) => {
      const count = item?.inventoryCount ?? (arr.length - index);
      const inventoryNumber = item?.inventoryNumber || formatInventoryNumber(item, count);
      return {
        ...item,
        inventoryCount: count,
        inventoryNumber,
      };
    });
  }, [rawInventory]);
  const registerInventoryMutation = RqRegisterInventory();

  const activeProducts = inventoryList.filter(
    p => p?.status === 'ACTIVE',
  ).length;

  const totalLots = inventoryList.reduce(
    (a, p) => a + (p?.lots?.length || 0),
    0,
  );

  const handleAddLot = item => {
    setSelectedInventoryItem(item);
    setView('addlot');
  };

  const handleBack = () => {
    setView('list');
    setSelectedInventoryItem(null);
  };

  const handleSuccess = () => {
    refetch();
    setView('list');
    setSelectedInventoryItem(null);
  };

  const handleCreateInventory = async () => {
    try {
      const today = new Date().toLocaleDateString('en-CA');
      const alreadyExists = inventoryList.some(item => {
        if (!item?.createdOn) {
          return false;
        }
        return new Date(item.createdOn).toLocaleDateString('en-CA') === today;
      });

      if (alreadyExists) {
        message.error('Today inventory already created');
        return;
      }

      await registerInventoryMutation.mutateAsync({
        payload: {status: 'ACTIVE'},
        token,
      });
      message.success('Inventory created successfully');
      refetch();
    } catch (error) {
      console.log(error);
      message.error(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to create inventory',
      );
    }
  };

  return (
    <div className={'md:p-6'}>
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #E83F25, #A62C2C)'}}
          >
            <MdOutlineInventory style={{fontSize: 22}} />
          </div>
          <Title level={3} className={'header !mb-0'}>
            Inventory
          </Title>
        </div>

        {view === 'list' && (
          <Button
            type={'primary'}
            size={'large'}
            icon={<PlusCircleOutlined />}
            loading={registerInventoryMutation.isPending}
            onClick={handleCreateInventory}
            style={{background: '#3a5f98'}}
          >
            Create Inventory
          </Button>
        )}
      </div>

      <div className={'grid grid-cols-2 md:grid-cols-3 gap-3 mb-6'}>
        <StatCard
          label={'Total Inventory'}
          value={inventoryList.length}
          icon={<AppstoreOutlined />}
        />
        <StatCard label={'Active'} value={activeProducts} icon={<FaCheck />} />
        <StatCard label={'Total Lots'} value={totalLots} icon={<FaTruckFast />} />
      </div>

      {view === 'list' && (
        <div className={'bg-white border border-primary-100 rounded-xl p-5 shadow-sm'}>
          <div className={'flex items-center gap-2 mb-4'}>
            <UnorderedListOutlined className={'text-gray-400'} />
            <span className={'text-sm font-semibold text-gray-700'}>
              All Inventory
            </span>
          </div>
          <InventoryListView onAddLot={handleAddLot} />
        </div>
      )}

      {view === 'addlot' && selectedInventoryItem && (
        <AddLotView
          inventoryItem={selectedInventoryItem}
          onBack={handleBack}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
};

export default InventoryScreen;
